import { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { t } from "i18next";
import * as GoalsRestClient from "../services/GoalsRestClient";
import {
  buildBracket,
  getRoundLockTime,
  getRoundWindow,
  getSchedule,
  isRoundDecided,
  isRoundLocked,
} from "../services/tournament";
import { TeamScore, TournamentState } from "../types/Tournament";
import { I18nString } from "../types/I18nString";
import {
  BRACKET_SIZE,
  DEBUG_FORCE_HIGHER_SEED_WINNER,
  GROUP_SIZE,
  ROUND_ACTIVE_DAYS,
  ROUND_LOCK_HOURS,
  ROUND_PAUSE_DAYS,
  TOURNAMENT_START,
} from "../config";
import { TableContainer, TitleContainer, Text } from "./styles";
import RoundNav from "./RoundNav";
import GroupCard from "./GroupCard";

type Props = {
  locale: keyof I18nString;
  ownTeamIds: Set<string>;
};

// Effektives Timing (aus der DB-Config, sonst aus den Config-Konstanten).
type Timing = {
  start: number;
  activeDays: number;
  pauseDays: number;
  lockHours: number;
};

type BracketState = TournamentState & { timing: Timing };

const buildScoreMap = (teams: TeamScore[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const team of teams) {
    map.set(team.id, team.score);
  }
  return map;
};

const TournamentBracket = ({ locale, ownTeamIds }: Props) => {
  const [state, setState] = useState<BracketState>();
  const [selectedRound, setSelectedRound] = useState(0);

  const load = useCallback(async () => {
    const now = Date.now();

    // Single Source of Truth: config + seeds + (gelockte) Rundenergebnisse.
    const data = await GoalsRestClient.getTourneyResults();
    const timing: Timing = {
      start: data.config?.tournamentStart ?? TOURNAMENT_START,
      activeDays: data.config?.roundActiveDays ?? ROUND_ACTIVE_DAYS,
      pauseDays: data.config?.roundPauseDays ?? ROUND_PAUSE_DAYS,
      lockHours: data.config?.roundLockHours ?? ROUND_LOCK_HOURS,
    };

    const seededTeams = data.seeds;
    const seedTeamIds = Array.from(seededTeams.values()).map((team) => team.id);

    const schedule = getSchedule(
      now,
      timing.start,
      timing.activeDays,
      timing.pauseDays,
      BRACKET_SIZE,
      GROUP_SIZE
    );

    const roundScores = new Map<number, Map<string, number>>();
    const decided = new Set<number>();
    const lastRoundToLoad =
      schedule.phase === "pre" ? -1 : schedule.currentRoundIndex;

    for (let r = 0; r <= lastRoundToLoad; r++) {
      const persistedScores = data.rounds.get(r);
      if (persistedScores) {
        // Runde ist gelockt und persistiert -> verbindliches Ergebnis.
        roundScores.set(r, persistedScores);
        decided.add(r);
        continue;
      }

      // Laufende, noch nicht gelockte Runde -> live als Vorschau berechnen.
      const window = getRoundWindow(r, timing.start, timing.activeDays, timing.pauseDays);
      const lockTime = getRoundLockTime(
        r,
        timing.start,
        timing.activeDays,
        timing.pauseDays,
        timing.lockHours
      );
      const ranking = await GoalsRestClient.getTeamScoresInWindow(
        window.start,
        window.end,
        lockTime,
        seedTeamIds
      );
      roundScores.set(r, buildScoreMap(ranking));
      if (isRoundDecided(r, now, timing.start, timing.activeDays, timing.pauseDays)) {
        decided.add(r);
      }
    }

    const rounds = buildBracket(
      seededTeams,
      BRACKET_SIZE,
      GROUP_SIZE,
      roundScores,
      decided,
      DEBUG_FORCE_HIGHER_SEED_WINNER
    );
    setState({ rounds, schedule, timing });
    setSelectedRound(schedule.currentRoundIndex);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) {
    return (
      <TableContainer>
        <TitleContainer>
          <Text fontSize={18} fontColor={"#000000"} fontWeight={"bold"}>
            {t("TOURNAMENT_TITLE")}
          </Text>
        </TitleContainer>
        <LoadingWrapper>
          <Spinner />
          <Text fontSize={14} fontColor={"#555555"}>
            {t("LOADING")}
          </Text>
        </LoadingWrapper>
      </TableContainer>
    );
  }

  const { start, activeDays, pauseDays, lockHours } = state.timing;
  const startDateLabel = new Date(start).toLocaleDateString(locale);
  const round = state.rounds[selectedRound];
  // Sieger hervorheben, wenn die ausgewählte Runde bereits entschieden ist.
  const showWinner =
    DEBUG_FORCE_HIGHER_SEED_WINNER ||
    isRoundDecided(selectedRound, Date.now(), start, activeDays, pauseDays);

  const locked = isRoundLocked(
    selectedRound,
    Date.now(),
    start,
    activeDays,
    pauseDays,
    lockHours
  );

  return (
    <TableContainer>
      <TitleContainer>
        <Text fontSize={18} fontColor={"#000000"} fontWeight={"bold"}>
          {t("TOURNAMENT_TITLE")}
        </Text>
      </TitleContainer>
      <NavWrapper>
        <RoundNav
          selectedRoundIndex={selectedRound}
          bracketSize={BRACKET_SIZE}
          groupSize={GROUP_SIZE}
          schedule={state.schedule}
          startDateLabel={startDateLabel}
          locked={locked}
          onPrev={() => setSelectedRound((r) => Math.max(0, r - 1))}
          onNext={() =>
            setSelectedRound((r) => Math.min(state.schedule.totalRounds - 1, r + 1))
          }
        />
      </NavWrapper>
      <MatchList>
        {round.groups.map((group) => (
          <GroupCard
            key={`r${group.roundIndex}g${group.groupIndex}`}
            group={group}
            showWinner={showWinner}
            locale={locale}
            ownTeamIds={ownTeamIds}
          />
        ))}
      </MatchList>
    </TableContainer>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 16px;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e0e0e0;
  border-top-color: #1565c0;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const NavWrapper = styled.div`
  padding: 10px 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const MatchList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 12px 12px 12px;
`;

export default TournamentBracket;
